import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Field } from 'redux-form'
import { cx } from '@emotion/css'
import debounce from 'lodash/debounce'

import { emailText, minLength, requiredField, useMediaQuery, Notification } from '@offerzen/pinata-shame'

import TextInput from '../TextInput'
import FormLabel from '../FormLabel'
import EmailInput from '../EmailInput'
import Button from '../Button'
import ButtonLoader from '../ButtonLoader'
import Checkbox from '../Checkbox'
import SingleAutocomplete from '../SingleAutocomplete'

import styles from './styles'

const validators = {
  required: requiredField('This field is required.'),
  minPasswordLength: minLength(14, 'Your password must contain at least 14 characters.'),
  email: emailText('You need to fill in a valid email address'),
  termsAndConditions: value => (value ? undefined : 'You need to agree to the terms and conditions to continue.'),
  requiredBasedInLocation: (value, allValues) => {
    const validationMessage = 'You need to select your current location.'
    if (!value) {
      return validationMessage
    }
    if (!allValues.based_in_location) {
      return validationMessage
    }
    if (!allValues.based_in_location.geoapify_place_id) {
      return validationMessage
    }
    if (value !== allValues.based_in_location.inputted_address) {
      return validationMessage
    }
    return undefined
  },
}

const renderErrorMessages = candidate => {
  if (candidate && candidate.error && candidate.provider === 'email' && !candidate.authState) {
    return (
      <Notification theme="error" className={styles.errorNotification}>
        {candidate.error === 'Email is already taken' ? (
          <div>Oops, there's been an error. Please check your email for further instructions.</div>
        ) : (
          candidate.error
        )}
      </Notification>
    )
  }
}

const SignUpForm = ({ onSubmit, places, candidate, addCity, searchCities, clearData, clearPlace, loading }) => {
  const { isPhoneOrLarger } = useMediaQuery()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchCitiesDebounce = useCallback(debounce(searchCities, 150), [])

  return (
    <form onSubmit={onSubmit}>
      <div className={styles.nameFieldsContainer}>
        <div className={cx(styles.nameField, styles.firstNameField)}>
          <FormLabel label="First name" htmlFor="first_name" />
          <Field
            id="first_name"
            name="first_name"
            className={cx(styles.nameFieldInput, styles.textInput)}
            validate={validators.required}
            component={TextInput}
            theme="primary"
            placeholder="Enter your first name"
          />
        </div>

        <div className={cx(styles.nameField, styles.lastNameField)}>
          <FormLabel label="Last name" htmlFor="last_name" />
          <Field
            id="last_name"
            name="last_name"
            className={cx(styles.nameFieldInput, styles.textInput)}
            validate={validators.required}
            component={TextInput}
            placeholder="Enter your last name"
          />
        </div>
      </div>

      <FormLabel label="Current location" htmlFor="location" />
      <Field
        id="location"
        name="location"
        placeholder="Search for your city"
        data={places.data}
        normalizer={suggestion => suggestion.name}
        onChange={clearPlace}
        onSelect={suggestion => addCity(suggestion)}
        onDataFetch={value => handleSearchCitiesDebounce(value)}
        onDataClear={() => clearData()}
        className={cx(styles.textInput, styles.searchInput)}
        errorClassName={styles.searchError}
        loading={places.fetching}
        validate={validators.requiredBasedInLocation}
        component={SingleAutocomplete}
      />

      <FormLabel label="Personal email address" htmlFor="email" />
      <Field
        id="email"
        name="email"
        className={cx(styles.inputReset, styles.textInput)}
        type="email"
        validate={validators.email}
        component={EmailInput}
        placeholder="Enter your email address"
      />

      <FormLabel label="Password" htmlFor="password" />
      <Field
        id="password"
        name="password"
        type="password"
        validate={validators.minPasswordLength}
        component={TextInput}
        className={cx(styles.inputReset, styles.textInput)}
        helpText="Your password must contain at least 14 characters"
        placeholder="Choose your password"
      />

      <Field
        id="opt_into_marketing"
        name="opt_into_marketing"
        component={Checkbox}
        theme="primary"
        label={
          <span>
            Let me know about job opportunities on OfferZen and other tech community updates. This includes upcoming tech events and content
            from our blog. Unsubscribe at any time.
          </span>
        }
        className={styles.checkbox}
      />

      <Field
        id="accepted_terms_of_service"
        name="accepted_terms_of_service"
        validate={validators.termsAndConditions}
        component={Checkbox}
        theme="primary"
        label={
          <span>
            I agree to OfferZen’s{' '}
            <a href="/terms/candidate" target="_blank">
              T&amp;Cs
            </a>
            ,{' '}
            <a href="/privacy" target="_blank">
              Privacy Policy
            </a>
            , and{' '}
            <a href="/cookie-declaration/" target="_blank">
              use of cookies
            </a>
            . I also consent to receiving communications that help with my job search.
          </span>
        }
        className={styles.checkbox}
      />
      {renderErrorMessages(candidate)}

      <Choose>
        <When condition={loading}>
          <ButtonLoader theme="primary" label="Signing up" className={styles.button} size={isPhoneOrLarger ? 'medium' : 'large'} />
        </When>

        <Otherwise>
          <Button type="submit" className={styles.button} size={isPhoneOrLarger ? 'medium' : 'large'} label="Create free profile" />
        </Otherwise>
      </Choose>
    </form>
  )
}

SignUpForm.propTypes = {
  onSubmit: PropTypes.func,
  places: PropTypes.shape({
    data: PropTypes.shape({}),
    fetching: PropTypes.bool,
  }),
  candidate: PropTypes.shape({}),
  addCity: PropTypes.func,
  searchCities: PropTypes.func,
  clearData: PropTypes.func,
  clearPlace: PropTypes.func,
  loading: PropTypes.bool,
}

export default SignUpForm
